/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import * as React from 'react';
import { SERVER_URL } from 'Constants';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import ApplicationUserClientEntity from 'Models/Security/Acl/ApplicationUserClientEntity';
import AgencyPlaylisterClientEntity from 'Models/Security/Acl/AgencyPlaylisterClientEntity';
import MustardAdminClientEntity from 'Models/Security/Acl/MustardAdminClientEntity';
import MustardManagementClientEntity from 'Models/Security/Acl/MustardManagementClientEntity';
import AgencyAdminClientEntity from 'Models/Security/Acl/AgencyAdminClientEntity';
import VisitorsClientEntity from 'Models/Security/Acl/VisitorsClientEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IClientEntityAttributes extends IModelAttributes {
	name: string;
	logoId: string;
	logo: Blob;
	agencyOwnerId: string;
	archived: boolean;

	agencyId?: string;
	agency?: Models.AgencyEntity | Models.IAgencyEntityAttributes;
	contactss: Array<
		| Models.ContactEntity
		| Models.IContactEntityAttributes
	>;
	venuess: Array<
		| Models.VenueEntity
		| Models.IVenueEntityAttributes
	>;
	// % protected region % [Add any custom attributes to the interface here] on begin
	numVenues: number;
	numZones: number;
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('ClientEntity', 'Client')
// % protected region % [Customise your entity metadata here] end
export default class ClientEntity extends Model
	implements IClientEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new ApplicationUserClientEntity(),
		new AgencyPlaylisterClientEntity(),
		new MustardAdminClientEntity(),
		new MustardManagementClientEntity(),
		new AgencyAdminClientEntity(),
		new VisitorsClientEntity(),
		// % protected region % [Add any further ACL entries here] on begin
		new AgencyAdminClientEntity(),
		new AgencyPlaylisterClientEntity(),
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		// % protected region % [Modify the order by field here] off begin
		return {
			path: 'name',
			descending: false,
		};
		// % protected region % [Modify the order by field here] end
	}

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@Validators.Required()
	@observable
	@attribute<ClientEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	// % protected region % [Modify props to the crud options here for attribute 'Logo'] on begin
	@observable
	@attribute({file: 'logo'})
	@CRUD({
		name: 'Logo',
		displayType: 'file',
		order: 20,
		headerColumn: false,
		searchable: false,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		inputProps: {
			imageOnly: true,
		},
		fileAttribute: 'logo',
		displayFunction: attr => attr 
			? <img src={`${SERVER_URL}/api/files/${attr}`} alt='A Client' style={{maxWidth: '300px'}} />
			: 'No File Attached',
	})
	public logoId: string;
	@observable
	public logo: Blob;
	// % protected region % [Modify props to the crud options here for attribute 'Logo'] end

	// % protected region % [Modify props to the crud options here for attribute 'Agency Owner Id'] on begin
	@Validators.Uuid()
	@observable
	@attribute()
	@CRUD({
		name: 'Agency Owner Id',
		displayType: 'textfield',
		order: 30,
		headerColumn: false,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public agencyOwnerId: string;
	// % protected region % [Modify props to the crud options here for attribute 'Agency Owner Id'] end

	// % protected region % [Modify props to the crud options here for attribute 'Archived'] off begin
	@observable
	@attribute<ClientEntity, boolean>()
	@CRUD({
		name: 'Archived',
		displayType: 'checkbox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public archived: boolean;
	// % protected region % [Modify props to the crud options here for attribute 'Archived'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Agency'] off begin
		name: 'Agency',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.AgencyEntity,
		// % protected region % [Modify props to the crud options here for reference 'Agency'] end
	})
	public agencyId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public agency: Models.AgencyEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Contacts'] off begin
		name: 'Contactss',
		displayType: 'reference-multicombobox',
		order: 60,
		referenceTypeFunc: () => Models.ContactEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'contactss',
			oppositeEntity: () => Models.ContactEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Contacts'] end
	})
	public contactss: Models.ContactEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Venues'] off begin
		name: 'Venuess',
		displayType: 'reference-multicombobox',
		order: 70,
		referenceTypeFunc: () => Models.VenueEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'venuess',
			oppositeEntity: () => Models.VenueEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Venues'] end
	})
	public venuess: Models.VenueEntity[] = [];

	// % protected region % [Add any custom attributes to the model here] on begin
	@observable
	@CRUD({
		name: 'No. Venues',
		displayType: 'displayfield',
		headerColumn: true,
		createFieldType: 'hidden',
		readFieldType: 'hidden',
		updateFieldType: 'hidden',
	})
	public numVenues = 0;

	@observable
	@CRUD({
		name: 'No. Zones',
		displayType: 'displayfield',
		headerColumn: true,
		createFieldType: 'hidden',
		readFieldType: 'hidden',
		updateFieldType: 'hidden',
	})
	public numZones = 0;
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IClientEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IClientEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.logo !== undefined) {
				this.logo = attributes.logo;
			}
			if (attributes.logoId !== undefined) {
				this.logoId = attributes.logoId;
			}
			if (attributes.agencyOwnerId !== undefined) {
				this.agencyOwnerId = attributes.agencyOwnerId;
			}
			if (attributes.archived !== undefined) {
				this.archived = attributes.archived;
			}
			if (attributes.agencyId !== undefined) {
				this.agencyId = attributes.agencyId;
			}
			if (attributes.agency !== undefined) {
				if (attributes.agency === null) {
					this.agency = attributes.agency;
				} else if (attributes.agency instanceof Models.AgencyEntity) {
					this.agency = attributes.agency;
					this.agencyId = attributes.agency.id;
				} else {
					this.agency = new Models.AgencyEntity(attributes.agency);
					this.agencyId = this.agency.id;
				}
			}
			if (attributes.contactss !== undefined && Array.isArray(attributes.contactss)) {
				for (const model of attributes.contactss) {
					if (model instanceof Models.ContactEntity) {
						this.contactss.push(model);
					} else {
						this.contactss.push(new Models.ContactEntity(model));
					}
				}
			}
			if (attributes.venuess !== undefined && Array.isArray(attributes.venuess)) {
				for (const model of attributes.venuess) {
					if (model instanceof Models.VenueEntity) {
						this.venuess.push(model);
					} else {
						this.venuess.push(new Models.VenueEntity(model));
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] on begin
			if (attributes.numVenues) {
				this.numVenues = attributes.numVenues;
			}
			if (attributes.numZones) {
				this.numZones = attributes.numZones;
			}
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		agency {
			${Models.AgencyEntity.getAttributes().join('\n')}
		}
		contactss {
			${Models.ContactEntity.getAttributes().join('\n')}
		}
		venuess {
			${Models.VenueEntity.getAttributes().join('\n')}
			${Models.VenueEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			contactss: {},
			venuess: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'contactss',
							'venuess',
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] on begin
	public listExpands = `
		numVenues
		numZones
		`;
	
	public clientPageExpands = `
		agency {
			${Models.AgencyEntity.getAttributes().join('\n')}
		}
		contactss {
			${Models.ContactEntity.getAttributes().join('\n')}
		}
		venuess {
			${Models.VenueEntity.getAttributes().join('\n')}
			${Models.VenueEntity.getFiles().map(f => f.name).join('\n')}
			venueType {
				${Models.VenueTypeEntity.getAttributes().join('\n')}
			}
			contactss {
				${Models.ContactEntity.getAttributes().join('\n')}
			}
			zoness {
				${Models.ZoneEntity.getAttributes().join('\n')}
				${Models.ZoneEntity.getFiles().map(f => f.name).join('\n')}
			}
			country {
				${Models.CountryEntity.getAttributes().join('\n')}
			}
			region {
				${Models.RegionEntity.getAttributes().join('\n')}
			}
		}
	`;
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ClientEntity.prototype, 'created');
CRUD(modifiedAttr)(ClientEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
